import { useEffect } from "react";
import { useDropzone } from "react-dropzone";
import CloudUpload from "@mui/icons-material/CloudUpload";
import { Box, CircularProgress, Typography } from "@mui/material";

import { useOrthoWorkflows } from "context/OrthoWorkflow";
import {
  formatFileSize,
  getFileRelativePath,
  isZipFile,
} from "context/OrthoWorkflow/orthoUpload";

interface OrthoFolderDropzoneProps {}

const OrthoFolderDropzone: React.FC<OrthoFolderDropzoneProps> = () => {
  const {
    upload: {
      activeSession: activeOrthoUploadSession,
      addFiles,
      analyzeFolder,
      setErrorMessage,
    },
  } = useOrthoWorkflows();

  const isAnalyzing = activeOrthoUploadSession?.isAnalyzing ?? false;
  const selectedFiles = activeOrthoUploadSession?.selectedFiles ?? null;
  const uploadStatus = activeOrthoUploadSession?.status ?? "idle";
  const selectedZip =
    selectedFiles?.length === 1 && isZipFile(selectedFiles[0])
      ? selectedFiles[0]
      : null;

  const isBusy =
    isAnalyzing || uploadStatus === "uploading" || uploadStatus === "hashing";

  const { fileRejections, getInputProps, getRootProps, isDragActive } =
    useDropzone({
      disabled: isBusy,
      multiple: true,
      onDrop: (acceptedFiles) => {
        if (acceptedFiles.length > 0) {
          void analyzeFolder(acceptedFiles);
        }
      },
    });

  useEffect(() => {
    if (selectedFiles && selectedFiles.length > 0) {
      addFiles(selectedFiles);
    }
  }, [selectedFiles, addFiles]);

  // Surface dropzone rejections through the shared error banner (rendered
  // outside the dialog) instead of a local, dialog-scoped message.
  useEffect(() => {
    if (fileRejections.length > 0) {
      setErrorMessage(
        fileRejections
          .map(
            ({ errors, file }) =>
              `${file.name} - ${errors.map((error) => error.message).join(", ")}`,
          )
          .join("; "),
      );
    }
  }, [fileRejections, setErrorMessage]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        {...getRootProps()}
        aria-label="Drop a folder of photos"
        sx={{
          "&:hover": {
            bgcolor: isBusy ? undefined : "action.hover",
          },
          alignItems: "center",
          bgcolor: isDragActive ? "action.hover" : "background.paper",
          borderColor: "divider",
          borderRadius: 1,
          borderStyle: "dashed",
          borderWidth: 1,
          cursor: isBusy ? "default" : "pointer",
          display: "flex",
          justifyContent: "center",
          minHeight: 120,
          px: 2,
          py: 2,
          textAlign: "center",
        }}
      >
        <input
          {...getInputProps({
            webkitdirectory: "",
            directory: "",
          } as React.InputHTMLAttributes<HTMLInputElement>)}
        />
        {isAnalyzing ? (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <CircularProgress aria-label="Analyzing folder" size={28} />
            <Typography variant="body2">Analyzing images…</Typography>
          </Box>
        ) : selectedFiles != null && selectedFiles.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="body2">
              {selectedZip ? "Selected zip: " : "Selected folder: "}
              {selectedZip
                ? selectedZip.name
                : getFileRelativePath(selectedFiles[0]).split("/")[0]}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {selectedZip
                ? formatFileSize(selectedZip.size)
                : `${selectedFiles.length} files`}
            </Typography>
          </Box>
        ) : isDragActive ? (
          <Typography variant="body2">Drop the folder here</Typography>
        ) : (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <CloudUpload color="primary" />
            <Box>
              <Typography variant="body2">
                Drag the flight folder here, or choose a folder
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ display: "block" }}
                variant="caption"
              >
                The whole folder goes up — JPEG frames straight off the drone,
                GPS read from each one’s EXIF.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OrthoFolderDropzone;
